'use client';

import * as m from 'motion/react-m';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';

import { Banner } from '@flipster/apis/v1/banner/types';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

import { VideoSlide } from './video-slide';

interface RollingBannerProps {
  items: Banner[];
  intervalMs?: number;
}

export const RollingBanner: React.FC<RollingBannerProps> = ({ items, intervalMs = 4000 }) => {
  const { isDesktop } = useBreakpoint();
  const total = items.length;
  const isMounted = useMounted();

  // Start index is 1 if there are clones (first real slide), otherwise 0
  const [index, setIndex] = useState(total > 1 ? 1 : 0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Clone array at both ends for seamless looping
  const slides = useMemo(() => {
    if (total <= 1) return items;
    return [items[total - 1], ...items, items[0]];
  }, [items, total]);

  const slidesCount = slides.length; // total <= 1 ? total : total + 2
  const stepPct = slidesCount > 0 ? 100 / slidesCount : 100;

  const animateX = `-${index * stepPct}%`;
  const TRANSITION_SEC = 0.5;

  useEffect(() => {
    // Reset index when items change
    setIndex(total > 1 ? 1 : 0);
  }, [total]);

  useEffect(() => {
    if (total <= 1) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function tick() {
      if (typeof document !== 'undefined' && document.hidden) {
        timer = setTimeout(tick, 300);
        return;
      }
      setIsAnimating(true);
      setIndex((i) => (i + 1) % slidesCount);
      timer = setTimeout(tick, intervalMs);
    }

    timer = setTimeout(tick, intervalMs);
    return () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
  }, [total, intervalMs, slidesCount]);

  // Instead of relying on animation end event, safely jump when reaching the last clone using timeout
  useEffect(() => {
    if (total <= 1) return;
    if (index === slidesCount - 1) {
      const t = setTimeout(() => {
        setIsAnimating(false);
        setIndex(1);
      }, TRANSITION_SEC * 1000);
      return () => clearTimeout(t);
    }
  }, [index, slidesCount, total]);

  if (total === 0) return null;

  const visibleIndex = total > 1 ? (index - 1 + total) % total : 0;

  return (
    <div className={wrapperCls}>
      <m.div
        className={css({ display: 'flex' })}
        style={{ width: `${slidesCount * 100}%` }}
        animate={{ x: animateX }}
        transition={{ type: 'tween', ease: 'easeInOut', duration: isAnimating ? TRANSITION_SEC : 0 }}
      >
        {slides.map((item, i) => {
          // Prevent hydration issues: use desktop image on server, responsive on client
          const imageSrc = !isMounted ? item.desktopImage : isDesktop ? item.desktopImage : item.mobileImage;
          const isClone = total > 1 && (i === 0 || i === slidesCount - 1);
          const videoSrc = isDesktop ? item.desktopVideo : item.mobileVideo ?? item.desktopVideo;
          const showVideo = isMounted && !isClone && !!videoSrc;
          return (
            <Link key={i} href={item.webLink ?? '/'} style={{ flex: `0 0 ${stepPct}%` }}>
              {showVideo ? (
                <VideoSlide
                  videoSrc={videoSrc}
                  posterSrc={imageSrc}
                  title={item.title}
                  isActive={i === index}
                  className={imgCls}
                />
              ) : (
                <img src={imageSrc} alt={item.title} width={590} className={imgCls} fetchPriority='high' />
              )}
            </Link>
          );
        })}
      </m.div>

      {/* dimmed background */}
      <div
        className={css({
          pos: 'absolute',
          h: 70,
          left: 0,
          right: 0,
          bottom: 0,
          bg: 'linear-gradient(180deg, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.4) 100%)',
        })}
      />

      {/* Progress bar */}
      {total > 1 && (
        <div
          className={center({
            pos: 'absolute',
            left: 0,
            right: 0,
            bottom: 16,
            gap: 8,
            pointerEvents: 'none',
          })}
        >
          {items.map((_, i) => {
            const state = i < visibleIndex ? 'done' : i === visibleIndex ? 'active' : 'todo';
            return (
              <div
                key={i}
                className={css({
                  w: 48,
                  h: 6,
                  bgColor: 'background.accent.secondary.tint',
                  rounded: 4,
                  overflow: 'hidden',
                })}
              >
                <m.div
                  key={state === 'active' ? `fill-active-${visibleIndex}` : `fill-static-${i}`}
                  className={css({
                    h: '100%',
                    bgColor: 'inverse.white',
                    rounded: 4,
                  })}
                  initial={{ width: state === 'active' ? 0 : state === 'done' ? '100%' : 0 }}
                  animate={{ width: state === 'active' ? '100%' : state === 'done' ? '100%' : '0%' }}
                  transition={{ duration: state === 'active' ? intervalMs / 1000 : 0, ease: 'linear' }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const wrapperCls = css({
  pos: 'relative',
  overflow: 'hidden',
  w: '100%',
  maxW: { tablet: 590 },
  rounded: { tablet: 16 },
  bgColor: 'background.default.level-1',
});
const imgCls = center({ w: '100%', maxW: { tablet: 590 }, aspectRatio: 590 / 520 });
